/**
 * The repeated furniture of the briefing: section shells, status pills, a risk
 * card that opens onto its evidence, the module grid, and the email dialog.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  ChevronDown, ChevronRight, ExternalLink, Loader2, Mail, Search, TrendingDown,
  TrendingUp, Minus, X,
} from 'lucide-react';

export const STATUS_META = {
  GREEN: { bg: 'rgba(16,185,129,0.15)', fg: '#10b981', label: 'GREEN' },
  AMBER: { bg: 'rgba(245,158,11,0.15)', fg: '#d97706', label: 'AMBER' },
  RED: { bg: 'rgba(239,68,68,0.15)', fg: '#dc2626', label: 'RED' },
  UNKNOWN: { bg: 'rgba(113,113,122,0.15)', fg: 'var(--text-muted)', label: 'NO DATA' },
};
const SEV = { CRITICAL: '#dc2626', HIGH: '#ea580c', MEDIUM: '#ca8a04' };
const TREND_ICON = { IMPROVING: TrendingUp, DETERIORATING: TrendingDown, FLAT: Minus, UNKNOWN: Minus };

export function StatusPill({ status, testId }) {
  const meta = STATUS_META[(status || 'UNKNOWN').toUpperCase()] || STATUS_META.UNKNOWN;
  return (
    <span data-testid={testId}
          className="text-[10px] font-semibold px-2 py-0.5 rounded-full tracking-wide"
          style={{ background: meta.bg, color: meta.fg }}>
      {meta.label}
    </span>
  );
}

export function Trend({ trend }) {
  const Icon = TREND_ICON[(trend || 'UNKNOWN').toUpperCase()] || Minus;
  const colour = trend === 'IMPROVING' ? '#10b981' : trend === 'DETERIORATING' ? '#dc2626' : '#9ca3af';
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium" style={{ color: colour }}>
      <Icon className="w-3 h-3" /> {(trend || 'UNKNOWN').toLowerCase()}
    </span>
  );
}

export function Section({ icon: Icon, title, note, children, testId, right }) {
  return (
    <section className="rounded-xl border border-border bg-card p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-4 h-4 text-foreground" />}
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        {note && <span className="text-[11px] text-muted-foreground">{note}</span>}
        {right && <div className="ml-auto">{right}</div>}
      </div>
      {children}
    </section>
  );
}

export function ModuleLink({ moduleKey, label }) {
  const navigate = useNavigate();
  if (!moduleKey) return null;
  return (
    <button onClick={() => navigate(`/ai-analyst?module=${encodeURIComponent(moduleKey)}`)}
            data-testid={`module-link-${moduleKey}`}
            className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground
                       hover:text-foreground transition-colors">
      {label || moduleKey} <ExternalLink className="w-3 h-3" />
    </button>
  );
}

export function RiskCard({ risk, index }) {
  const [open, setOpen] = useState(false);
  const colour = SEV[(risk.severity || 'HIGH').toUpperCase()] || SEV.HIGH;
  return (
    <div className="rounded-lg border border-border overflow-hidden" data-testid={`risk-card-${index}`}>
      <button onClick={() => setOpen(o => !o)} data-testid={`risk-toggle-${index}`}
              className="w-full flex items-start gap-3 p-4 text-left hover:bg-muted/40 transition-colors">
        <span className="w-8 h-8 rounded-full grid place-items-center text-xs font-bold shrink-0"
              style={{ background: `${colour}22`, color: colour }}>
          #{risk.rank || index + 1}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                  style={{ background: `${colour}22`, color: colour }}>
              {risk.severity}
            </span>
            <span className="text-[10px] font-mono text-muted-foreground">{risk.module}</span>
          </span>
          <span className="block text-sm font-semibold text-foreground mt-1">{risk.risk}</span>
          <span className="block text-[11px] text-muted-foreground mt-1">
            {risk.owner} · by {risk.by_when}
          </span>
        </span>
        {open ? <ChevronDown className="w-4 h-4 shrink-0 mt-1" />
              : <ChevronRight className="w-4 h-4 shrink-0 mt-1" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-border pt-3"
             data-testid={`risk-detail-${index}`}>
          <Field label="Evidence" value={risk.evidence} />
          {(risk.evidence_points || []).length > 0 && (
            <ul className="space-y-1">
              {risk.evidence_points.map((e, i) => (
                <li key={i} className="text-[11px] text-muted-foreground flex gap-2">
                  <span className="text-foreground/40">—</span><span>{e}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Impact" value={risk.impact} />
            <Field label="Exposure" value={risk.exposure} />
            <Field label="If ignored" value={risk.if_ignored} />
            <Field label="Source" value={<ModuleLink moduleKey={risk.module} />} />
          </div>
          <div className="rounded-lg p-3" style={{ background: 'rgba(16,185,129,0.08)' }}>
            <p className="text-[10px] uppercase tracking-wide text-emerald-600 font-semibold">
              Authorise
            </p>
            <p className="text-xs text-foreground mt-1">{risk.intervention}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export function Expandable({ title, meta, children, testId, tone }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-border overflow-hidden" data-testid={testId}>
      <button onClick={() => setOpen(o => !o)} data-testid={`${testId}-toggle`}
              className="w-full flex items-start gap-3 p-3 text-left hover:bg-muted/40 transition-colors">
        {tone && <span className="w-1 self-stretch rounded-full shrink-0" style={{ background: tone }} />}
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-foreground">{title}</span>
          {meta && <span className="block text-[11px] text-muted-foreground mt-0.5">{meta}</span>}
        </span>
        {open ? <ChevronDown className="w-4 h-4 shrink-0 mt-0.5" />
              : <ChevronRight className="w-4 h-4 shrink-0 mt-0.5" />}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-3 border-t border-border space-y-3"
             data-testid={`${testId}-detail`}>
          {children}
        </div>
      )}
    </div>
  );
}

export function SectionFold({ summary, children, label, testId, count }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      {summary}
      <div className="mt-3 pt-3 border-t border-border/60">
        <button onClick={() => setOpen(o => !o)} data-testid={`${testId}-toggle`}
                className="flex items-center gap-1.5 text-[11px] text-muted-foreground
                           hover:text-foreground transition-colors">
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          {open ? `Hide ${label}` : `Show ${label}`}
          {count > 0 && <span className="font-mono">· {count}</span>}
        </button>
        {open && <div className="mt-3" data-testid={`${testId}-detail`}>{children}</div>}
      </div>
    </div>
  );
}

export function Field({ label, value }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="text-xs text-foreground mt-0.5">{empty ? '—' : value}</div>
    </div>
  );
}

export function ModuleHealthGrid({ modules }) {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('ALL');
  const counts = useMemo(() => {
    const c = { GREEN: 0, AMBER: 0, RED: 0, UNKNOWN: 0 };
    (modules || []).forEach(m => { c[(m.status || 'UNKNOWN').toUpperCase()] = (c[(m.status || 'UNKNOWN').toUpperCase()] || 0) + 1; });
    return c;
  }, [modules]);
  const shown = useMemo(() => (modules || []).filter(m => {
    const s = (m.status || 'UNKNOWN').toUpperCase();
    if (status !== 'ALL' && s !== status) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return `${m.label} ${m.module} ${m.headline}`.toLowerCase().includes(q);
  }), [modules, status, query]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
          <input value={query} onChange={e => setQuery(e.target.value)}
                 placeholder="Search modules" data-testid="module-grid-search"
                 className="pl-8 pr-3 py-1.5 text-xs bg-muted/40 border border-border rounded-lg
                            outline-none focus:border-amber-500/60 w-52" />
        </div>
        {['ALL', 'RED', 'AMBER', 'GREEN', 'UNKNOWN'].map(s => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`module-grid-filter-${s}`}
                  className={`text-[10px] px-2.5 py-1.5 rounded-lg border transition-colors ${
                    status === s ? 'border-amber-500/60 bg-muted/60 text-foreground'
                                 : 'border-border text-muted-foreground hover:bg-muted/40'}`}>
            {s === 'ALL' ? `All ${(modules || []).length}` : `${STATUS_META[s].label} ${counts[s] || 0}`}
          </button>
        ))}
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map(m => (
          <div key={m.module} className="rounded-lg border border-border p-3 hover:border-amber-500/40
                                          transition-colors" data-testid={`module-card-${m.module}`}>
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-semibold text-foreground">{m.label}</p>
              <StatusPill status={m.status} testId={`module-status-${m.module}`} />
            </div>
            {m.headline_metric && (
              <p className="text-[11px] font-mono text-muted-foreground mt-1">{m.headline_metric}</p>
            )}
            <p className="text-[11px] text-muted-foreground mt-1.5 line-clamp-3">{m.headline}</p>
            <div className="mt-2"><ModuleLink moduleKey={m.module} label="open analyst" /></div>
          </div>
        ))}
        {shown.length === 0 && (
          <p className="text-xs text-muted-foreground" data-testid="module-grid-empty">
            No module matches that filter.
          </p>
        )}
      </div>
    </div>
  );
}

export function EmailDialog({ projectId, open, onClose }) {
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  if (!open) return null;

  const send = async () => {
    if (!to.trim()) { toast.error('Give at least one recipient'); return; }
    setSending(true);
    try {
      await api.post(`/v1/projects/${projectId}/ai-analyst/ceo-briefing/email`,
        { to: to.split(',').map(t => t.trim()).filter(Boolean), note }, { timeout: 120000 });
      toast.success('Briefing sent');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The briefing could not be emailed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
         data-testid="briefing-email-dialog" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl border border-border bg-background p-5 space-y-3"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4" />
          <p className="text-sm font-semibold text-foreground">Email this briefing</p>
          <button onClick={onClose} className="ml-auto p-1 rounded hover:bg-muted"
                  data-testid="briefing-email-close"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          The PDF goes as an attachment, with the headline in the body of the mail.
        </p>
        <input value={to} onChange={e => setTo(e.target.value)} data-testid="briefing-email-to"
               placeholder="name@company.com, second@company.com"
               className="w-full text-xs bg-muted/40 border border-border rounded-lg px-3 py-2
                          outline-none focus:border-amber-500/60" />
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
                  data-testid="briefing-email-note" placeholder="A line of context (optional)"
                  className="w-full text-xs bg-muted/40 border border-border rounded-lg px-3 py-2
                             outline-none focus:border-amber-500/60" />
        <button onClick={send} disabled={sending} data-testid="briefing-email-send"
                className="w-full py-2 rounded-lg bg-amber-500 text-black text-xs font-semibold
                           disabled:opacity-50 flex items-center justify-center gap-2">
          {sending && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Send
        </button>
      </div>
    </div>
  );
}
